import React from "react";

import { useDropzone } from "react-dropzone";
import { LuX } from "react-icons/lu";
import styled from "styled-components";

import {
  isExistingAttachment,
  UPLOAD_STATUS,
} from "shared/ui/upload/uploadItem";

const DEFAULT_PROMPT =
  "클릭하거나 드래그해서 첨부파일을 업로드하세요 (파일명 영문 권장)";

// 용량(byte) → 사람이 읽는 문자열. size 필드가 없으면 호출부에서 "-"로 대체한다.
const formatFileSize = (bytes) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

const fileKey = (file) =>
  `file_${file.localId ?? file.id ?? file.link ?? file.path ?? file.name ?? file.title}_${file.size ?? 0}`;

// 업로드 상태 → 표에 보일 라벨/스타일. 기존(서버) 첨부는 항상 완료.
const renderStatus = (file, isExisting) => {
  if (isExisting) return <StatusDone>완료</StatusDone>;
  switch (file.status) {
    case UPLOAD_STATUS.FAILED:
      return <StatusFail>업로드 실패</StatusFail>;
    case UPLOAD_STATUS.UPLOADING:
      return <StatusPending>업로드 중…</StatusPending>;
    case UPLOAD_STATUS.UPLOADED:
      return <StatusDone>완료</StatusDone>;
    default:
      return <StatusPending>저장 시 업로드</StatusPending>;
  }
};

// 같은 파일(이름+크기 동일)인지 — 중복 드롭 방지용
const isSameFile = (a, b) =>
  a === b || (a?.name != null && a.name === b?.name && a?.size === b?.size);

/**
 * 파일명 셀. 길어서 말줄임(…) 처리된 경우에만 hover 시 전체 이름을 툴팁으로
 * 보여준다. 업로드 실패 시에는 잘림 여부와 무관하게 에러 메시지를 툴팁으로 노출.
 */
function FileName({ name, error }) {
  const textRef = React.useRef(null);
  const [truncated, setTruncated] = React.useState(false);

  // hover 시점에 실제 넘침 여부를 측정(열 너비가 가변이라 사전 계산이 어렵다).
  const measure = () => {
    const el = textRef.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth);
  };

  const showTooltip = error ? true : truncated;

  return (
    <NameCell onMouseEnter={measure}>
      <NameText ref={textRef}>{name}</NameText>
      {showTooltip && (
        <NameTooltip role="tooltip" $error={Boolean(error)}>
          {error || name}
        </NameTooltip>
      )}
    </NameCell>
  );
}

/**
 * 공용 첨부파일 드롭존(표 형태).
 * 파일 상태는 부모가 소유한다(files + onChange). 추가/삭제 시 다음 목록 전체를
 * onChange로 돌려준다. 목록 항목은 새로 고른 브라우저 File({name,size})이거나
 * 서버에서 불러온 기존 첨부({id,title,link})일 수 있다.
 *
 * @param files      현재 첨부 목록 (혼합 가능)
 * @param onChange   (nextFiles) => void — 추가/삭제 후 전체 목록
 * @param promptText 비어있을 때 안내 문구
 * @param className  passthrough
 */
export function FileDropzone({
  files,
  onChange,
  promptText = DEFAULT_PROMPT,
  className,
}) {
  const list = Array.isArray(files) ? files : Array.from(files || []);

  const handleRemove = (target) => {
    onChange(list.filter((file) => file !== target));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const onDrop = (accepted) => {
    const fresh = accepted.filter(
      (file) => !list.some((existing) => isSameFile(existing, file)),
    );
    onChange([...list, ...fresh]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <DropzoneBox className={className} {...getRootProps()}>
      <input {...getInputProps()} />
      {list.length === 0 ? (
        <Prompt>{promptText}</Prompt>
      ) : (
        <Table>
          <HeaderRow>
            <DeleteCell>
              <IconButton
                type="button"
                aria-label="첨부파일 전체 삭제"
                onClick={(event) => {
                  event.stopPropagation();
                  handleClearAll();
                }}
              >
                <LuX size={16} />
              </IconButton>
            </DeleteCell>
            <HeaderCell>파일명</HeaderCell>
            <HeaderCell $center>업로드 상태</HeaderCell>
            <HeaderCell $center>용량</HeaderCell>
          </HeaderRow>
          {list.map((file) => {
            const isExisting = isExistingAttachment(file);
            const rawName =
              (isExisting ? file.title : file.name ?? file.path) ?? "첨부파일";
            const failed = !isExisting && file.status === UPLOAD_STATUS.FAILED;
            // size 필드가 있으면 표시, 없으면 "-" (서버가 추후 size를 채워줄 예정)
            const sizeText = Number.isFinite(file.size)
              ? formatFileSize(file.size)
              : "-";

            return (
              <Row key={fileKey(file)} $failed={failed}>
                <DeleteCell>
                  <IconButton
                    type="button"
                    aria-label="첨부파일 삭제"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemove(file);
                    }}
                  >
                    <LuX size={16} />
                  </IconButton>
                </DeleteCell>
                <FileName name={rawName} error={failed ? file.error : null} />
                <StatusCell>{renderStatus(file, isExisting)}</StatusCell>
                <SizeCell>{sizeText}</SizeCell>
              </Row>
            );
          })}
        </Table>
      )}
    </DropzoneBox>
  );
}

export default FileDropzone;

const DropzoneBox = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  width: 100%;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
`;

const Prompt = styled.div`
  width: 100%;
  padding: 18px 20px;
  font-size: 15px;
  font-weight: 400;
  font-style: normal;
  font-family: "Noto Sans KR";
  color: var(--admin-gray-disabled-button, #cfd4d9);
  text-align: center;
`;

const Table = styled.div`
  width: 100%;
  font-family: "Noto Sans KR";
  font-size: 14px;
`;

// 파일명에 폭 우선권(좁아져도 최소 72px·말줄임). 업로드 상태는 가장 긴 라벨
// ("저장 시 업로드")이 한 줄에 들어가도록 내용 폭(max-content)으로 잡아 줄바꿈을
// 막고, 용량은 적당히 유연하게 둔다.
const rowLayout = `
  display: grid;
  grid-template-columns: 40px minmax(72px, 1fr) max-content minmax(56px, 80px);
  align-items: center;
`;

const HeaderRow = styled.div`
  ${rowLayout}
  background: #f5f6f7;
  color: #8a8f99;
  font-size: 13px;
  border-bottom: 1px solid #ececec;

  > * {
    padding: 10px 8px;
  }
`;

const Row = styled.div`
  ${rowLayout}
  border-bottom: 1px solid #f0f0f0;
  color: #333;
  background: ${(props) => (props.$failed ? "#fff5f5" : "transparent")};

  &:last-child {
    border-bottom: none;
  }

  > * {
    padding: 10px 8px;
  }
`;

const HeaderCell = styled.div`
  text-align: ${(props) =>
    props.$right ? "right" : props.$center ? "center" : "left"};
`;

const DeleteCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 잘린 파일명 위에 뜨는 툴팁. 평소엔 숨고 NameCell hover 시 노출(아래 NameCell 참고).
const NameTooltip = styled.div`
  position: absolute;
  left: 8px;
  bottom: calc(100% - 4px);
  z-index: 5;
  width: max-content;
  max-width: 320px;
  padding: 6px 10px;
  border-radius: 6px;
  background: ${(props) => (props.$error ? "#e03131" : "#2b2f36")};
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.12s ease;
`;

const NameText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NameCell = styled.div`
  position: relative;
  min-width: 0;

  &:hover ${NameTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

const StatusCell = styled.div`
  text-align: center;
  white-space: nowrap;
`;

const SizeCell = styled.div`
  text-align: center;
  color: #8a8f99;
  white-space: nowrap;
`;

const StatusDone = styled.span`
  color: #2f9e44;
`;

const StatusPending = styled.span`
  color: #868e96;
`;

const StatusFail = styled.span`
  color: #e03131;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  padding: 0;
  background: transparent;
  color: #868e96;
  cursor: pointer;
  flex-shrink: 0;
`;
